import { getIdTokenResult, type User } from 'firebase/auth';

import { firebase, type UserCustomClaims } from '@gitbook/app-database';
import { Trace } from '@gitbook/util-debug';

const userHashesMap = new Map<string, Promise<string | null>>();
// A new application load fetches the signature again, including after a signing-secret rotation.
const cachedUserHashes = new WeakMap<User, string>();

/** Fetch the current Intercom signature for the user. */
export async function getIntercomUserHash(user: User): Promise<string | null>;
export async function getIntercomUserHash(user: null): Promise<null>;
export async function getIntercomUserHash(user: User | null): Promise<string | null> {
    if (!user) {
        return null;
    }

    const cachedHash = cachedUserHashes.get(user);
    if (cachedHash) {
        return cachedHash;
    }

    const userHash = userHashesMap.get(user.uid);
    if (userHash) {
        return userHash;
    }

    const pending = (async () => {
        const trace = new Trace('analytics.getIntercomUserHash');
        trace.label('userId', user.uid);

        return trace.wrap(async () => {
            try {
                const computeIntercomHash = firebase
                    .functions()
                    .httpsCallable('users-computeIntercomHash', { timeout: 5000 });
                const result = await computeIntercomHash();
                const { intercomUserHash } = result.data as Pick<
                    UserCustomClaims,
                    'intercomUserHash'
                >;
                if (intercomUserHash) {
                    try {
                        const token = await getIdTokenResult(user);
                        if (token.claims.intercomUserHash !== intercomUserHash) {
                            await getIdTokenResult(user, true);
                        }
                    } catch (_error) {
                        trace.label('tokenRefreshFailed', true);
                    }
                    cachedUserHashes.set(user, intercomUserHash);
                    return intercomUserHash;
                }
            } catch (_error) {
                trace.label('signatureFetchFailed', true);
            }

            // Keep claims as a fallback for function outages and older callable deployments.
            return getHashFromToken(user);
        });
    })();

    userHashesMap.set(user.uid, pending);
    try {
        return await pending;
    } finally {
        userHashesMap.delete(user.uid);
    }
}

/**
 * Try to get the hash from the user's token using the current claims.
 * If the hash is not available, try to get it from a fresh token.
 */
async function getHashFromToken(user: User): Promise<string | null> {
    // First we try to get the hash from the current token to prevent
    // refetching a new one, which is slow.
    try {
        const idTokenResult = await getIdTokenResult(user);
        const claims = idTokenResult.claims as UserCustomClaims;
        if (claims.intercomUserHash) {
            return claims.intercomUserHash;
        }
    } catch (_err) {
        /**/
    }

    try {
        const freshIdTokenResult = await getIdTokenResult(user, true);
        const claims = freshIdTokenResult.claims as UserCustomClaims;
        if (claims.intercomUserHash) {
            return claims.intercomUserHash;
        }
    } catch (_err) {
        /**/
    }

    return null;
}
